import { supabase } from "@/integrations/supabase/client";
import { convertPDFToText } from "./pdfConversionService";

export interface SavedResume {
  id: string;
  title: string;
  content?: string;
  fileName?: string;
  fileType?: string;
  originalFileUrl?: string;
  originalFilePath?: string;
  parsedResumeData?: any;
  processingStatus?: string;
  wordDocumentUrl?: string;
  wordDocumentPath?: string;
  dateCreated: string;
  matchScore?: number;
  jobTitle?: string;
  companyName?: string;
  analysisResults?: any;
}

export class ResumeLibraryService {
  /**
   * Get all saved resumes for the current user (excludes tailored resumes)
   */
  static async getSavedResumes(): Promise<SavedResume[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('resumes')
      .select('*')
      .eq('user_id', user.id) // Filter by current user
      .is('original_match_score', null) // Exclude tailored resumes
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching saved resumes:', error);
      throw new Error('Failed to fetch saved resumes');
    }

    console.log('ResumeLibraryService: Fetched resumes for user', user.id, ':', data.length);
    return data.map(resume => ({
      id: resume.id,
      title: resume.title,
      content: resume.content,
      fileName: resume.file_name,
      fileType: resume.file_type,
      originalFileUrl: resume.original_file_url,
      originalFilePath: resume.original_file_path,
      parsedResumeData: resume.parsed_resume_data,
      processingStatus: resume.processing_status,
      wordDocumentUrl: resume.word_document_url,
      wordDocumentPath: resume.word_document_path,
      dateCreated: resume.created_at,
      matchScore: resume.match_score,
      jobTitle: resume.job_title,
      companyName: resume.company_name,
      analysisResults: resume.analysis_results
    }));
  }

  /**
   * Save a new resume
   */
  static async saveResume(title: string, content?: string, fileName?: string, fileType?: string): Promise<SavedResume> {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      throw new Error('User must be authenticated to save resumes');
    }

    const { data, error } = await supabase
      .from('resumes')
      .insert({
        user_id: session.user.id,
        title,
        content,
        file_name: fileName || null,
        file_type: fileType || null
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving resume:', error);
      throw new Error('Failed to save resume');
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      fileName: data.file_name,
      fileType: data.file_type,
      originalFileUrl: data.original_file_url,
      originalFilePath: data.original_file_path,
      parsedResumeData: data.parsed_resume_data,
      processingStatus: data.processing_status,
      wordDocumentUrl: data.word_document_url,
      wordDocumentPath: data.word_document_path,
      dateCreated: data.created_at,
      matchScore: data.match_score,
      jobTitle: data.job_title,
      companyName: data.company_name,
      analysisResults: data.analysis_results
    };
  }

  /**
   * Save a resume with analysis data
   */
  static async saveResumeWithAnalysis(
    title: string, 
    content: string, 
    matchScore: number,
    jobTitle: string,
    companyName: string,
    analysisResults: any,
    fileName?: string, 
    fileType?: string
  ): Promise<SavedResume> {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      throw new Error('User must be authenticated to save resumes');
    }

    const { data, error } = await supabase
      .from('resumes')
      .insert({
        user_id: session.user.id,
        title,
        content,
        file_name: fileName || null,
        file_type: fileType || null,
        match_score: matchScore,
        job_title: jobTitle,
        company_name: companyName,
        analysis_results: analysisResults
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving resume:', error);
      throw new Error('Failed to save resume');
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      fileName: data.file_name || '',
      fileType: data.file_type || '',
      dateCreated: data.created_at,
      matchScore: data.match_score,
      jobTitle: data.job_title,
      companyName: data.company_name,
      analysisResults: data.analysis_results
    };
  }

  /**
   * Save a resume with file upload
   */
  static async saveResumeWithFile(title: string, file: File, parsedData?: any): Promise<SavedResume> {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      throw new Error('User must be authenticated to save resumes');
    }

    try {
      // Upload original file to storage first (needed for PDF processing)
      const fileExt = file.name.split('.').pop();
      const fileName = `${session.user.id}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false
        });

      if (uploadError) {
        throw new Error(`File upload failed: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(fileName);

      let extractedContent = '';
      
      // Handle PDF files with ConvertAPI
      if (file.type === 'application/pdf') {
        console.log('ResumeLibraryService: Processing PDF with ConvertAPI:', file.name);
        
        const conversionResult = await convertPDFToText(publicUrl);
        
        if (conversionResult.success && conversionResult.text) {
          extractedContent = conversionResult.text;
          console.log('ResumeLibraryService: Successfully extracted', extractedContent.length, 'characters using ConvertAPI');
        } else {
          console.warn('ResumeLibraryService: ConvertAPI failed:', conversionResult.error);
          // Fallback to DocumentProcessor for PDFs if ConvertAPI fails
          console.log('ResumeLibraryService: Falling back to DocumentProcessor for PDF');
          const { DocumentProcessor } = await import('@/utils/documentProcessor');
          const processingResult = await DocumentProcessor.processDocument(file);
          extractedContent = processingResult.text || '';
        }
      } else {
        // Use DocumentProcessor for non-PDF files
        console.log('ResumeLibraryService: Processing non-PDF file with DocumentProcessor:', file.name, file.type);
        const { DocumentProcessor } = await import('@/utils/documentProcessor');
        const processingResult = await DocumentProcessor.processDocument(file);
        extractedContent = processingResult.text || '';
        console.log('ResumeLibraryService: Successfully extracted', extractedContent.length, 'characters using', processingResult.processingMethod);
      }

      if (!extractedContent) {
        console.warn('ResumeLibraryService: No text extracted from document');
      }

      // Save resume record to database with extracted content
      console.log('ResumeLibraryService: Saving to database with content length:', extractedContent.length);
      const { data, error } = await supabase
        .from('resumes')
        .insert({
          user_id: session.user.id,
          title,
          content: extractedContent, // Store the extracted text
          file_name: file.name,
          file_type: file.type,
          original_file_url: publicUrl,
          original_file_path: fileName,
          parsed_resume_data: parsedData,
          processing_status: extractedContent ? 'parsed' : 'uploaded'
        })
        .select()
        .single();
      
      console.log('ResumeLibraryService: Database save result:', { success: !error, contentSaved: data?.content?.length || 0 });

      if (error) {
        // Clean up uploaded file if database insert fails
        await supabase.storage.from('resumes').remove([fileName]);
        throw new Error(`Database save failed: ${error.message}`);
      }

      return {
        id: data.id,
        title: data.title,
        content: data.content,
        fileName: data.file_name,
        fileType: data.file_type,
        originalFileUrl: data.original_file_url,
        originalFilePath: data.original_file_path,
        parsedResumeData: data.parsed_resume_data,
        processingStatus: data.processing_status,
        wordDocumentUrl: data.word_document_url,
        wordDocumentPath: data.word_document_path,
        dateCreated: data.created_at,
        matchScore: data.match_score,
        jobTitle: data.job_title,
        companyName: data.company_name,
        analysisResults: data.analysis_results
      };
    } catch (error) {
      console.error('Error saving resume with file:', error);
      throw error;
    }
  }

  /**
   * Update Word document details
   */
  static async updateWordDocument(resumeId: string, wordDocumentUrl: string, wordDocumentPath: string): Promise<SavedResume> {
    const { data, error } = await supabase
      .from('resumes')
      .update({
        word_document_url: wordDocumentUrl,
        word_document_path: wordDocumentPath,
        processing_status: 'word_generated',
        updated_at: new Date().toISOString()
      })
      .eq('id', resumeId)
      .select()
      .single();

    if (error) {
      console.error('Error updating Word document:', error);
      throw new Error('Failed to update Word document');
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      fileName: data.file_name,
      fileType: data.file_type,
      originalFileUrl: data.original_file_url,
      originalFilePath: data.original_file_path,
      parsedResumeData: data.parsed_resume_data,
      processingStatus: data.processing_status,
      wordDocumentUrl: data.word_document_url,
      wordDocumentPath: data.word_document_path,
      dateCreated: data.created_at,
      matchScore: data.match_score,
      jobTitle: data.job_title,
      companyName: data.company_name,
      analysisResults: data.analysis_results
    };
  }
  static async deleteResume(resumeId: string): Promise<void> {
    const { error } = await supabase
      .from('resumes')
      .delete()
      .eq('id', resumeId);

    if (error) {
      console.error('Error deleting resume:', error);
      throw new Error('Failed to delete resume');
    }
  }

  /**
   * Update a saved resume
   */
  static async updateResume(resumeId: string, title: string, content: string): Promise<SavedResume> {
    const { data, error } = await supabase
      .from('resumes')
      .update({
        title,
        content,
        updated_at: new Date().toISOString()
      })
      .eq('id', resumeId)
      .select()
      .single();

    if (error) {
      console.error('Error updating resume:', error);
      throw new Error('Failed to update resume');
    }

    return {
      id: data.id,
      title: data.title,
      content: data.content,
      fileName: data.file_name || '',
      fileType: data.file_type || '',
      dateCreated: data.created_at
    };
  }
}